import './Login.css'

import type { ReactElement } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router'

import { Button } from '#design-system/component/Button/Button'
import { Input } from '#design-system/component/Form/Input/Input'
import { Banner } from '#design-system/component/Layout/Banner/Banner'
import { GoogleLogo } from '#design-system/icons/google-logo'
import { PaperPlaneTilt } from '#design-system/icons/paper-plane-tilt'
import { getLastAuthMethod } from '~/common/auth/last-auth-method'
import type { OAuthProvider } from '~/common/auth/providers'
import { useAuth } from '~/common/hooks/useAuth'

import { AuthLayout } from './AuthLayout'

/**
 * Extracts OAuth error key from URL params.
 */
const getOAuthErrorKeyFromUrl = (
  search: string,
): 'auth.oauth_account_not_found' | 'auth.oauth_failed' | null => {
  const params = new URLSearchParams(search)
  const oauthError = params.get('oauthError')
  if (oauthError) {
    return oauthError === 'account_not_found' ? 'auth.oauth_account_not_found' : 'auth.oauth_failed'
  }
  return null
}

export const Login = (): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn, signInWithOAuth, isSignedIn, isSigningIn } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Initialize error from URL params (OAuth callback errors)
  const [error, setError] = useState<string | null>(() => {
    const oauthErrorKey = getOAuthErrorKeyFromUrl(location.search)
    return oauthErrorKey ? t(oauthErrorKey) : null
  })

  // ResetPassword sends the user back here after a successful reset
  const isResetConfirmed = new URLSearchParams(location.search).get('resetSuccess') === 'true'

  // Which method signed this browser in last
  const [lastAuthMethod] = useState(getLastAuthMethod)

  // Track if we've cleaned up the URL
  const hasCleanedUrl = useRef(false)

  // Get redirect path from URL params (used after successful login)
  const redirectPath = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('redirect') ?? '/'
  }, [location.search])

  // Strip the consumed params once on mount (side effect only, no state change)
  useEffect(() => {
    if (hasCleanedUrl.current) {
      return
    }

    const params = new URLSearchParams(location.search)
    if (!params.has('oauthError') && !params.has('resetSuccess')) {
      return
    }

    hasCleanedUrl.current = true
    params.delete('oauthError')
    params.delete('resetSuccess')
    const nextSearch = params.toString()
    window.history.replaceState(
      null,
      '',
      `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
    )
  }, [location.pathname, location.search])

  // Redirect if already signed in
  useEffect(() => {
    if (isSignedIn) {
      void navigate(redirectPath, { replace: true })
    }
  }, [isSignedIn, navigate, redirectPath])

  const handleSubmit = async (): Promise<void> => {
    setError(null)

    try {
      await signIn(email, password)
      void navigate(redirectPath, { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.sign_in_failed')

      // Map common error messages to user-friendly translations
      if (message === 'Invalid credentials' || message.includes('Invalid')) {
        setError(t('auth.invalid_credentials'))
      } else {
        setError(message)
      }
    }
  }

  const handleOAuthSignIn = async (provider: OAuthProvider): Promise<void> => {
    setError(null)
    try {
      await signInWithOAuth(provider, redirectPath)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.oauth_failed')
      setError(message)
    }
  }

  return (
    <AuthLayout title={t('auth.log_in_to_doss')} type="login">
      {error !== null && (
        <Banner bodyText={error} headerText={t('auth.sign_in_error')} semanticType="Error" />
      )}

      {isResetConfirmed && error === null && (
        <Banner
          bodyText={t('auth.reset_success_body')}
          headerText={t('auth.reset_success')}
          semanticType="Success"
        />
      )}

      <div className="login">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit()
          }}
        >
          <Input
            autoComplete="email"
            label={t('auth.email')}
            onChange={setEmail}
            onLiveChange={setEmail}
            placeholder={t('auth.email_placeholder')}
            required
            size="full"
            type="email"
            value={email}
          />

          <div className="password-method">
            <Input
              autoComplete="current-password"
              label={t('auth.password')}
              onChange={setPassword}
              onLiveChange={setPassword}
              required
              size="full"
              type="password"
              value={password}
            />

            <Link className="forgot" to="/auth/forgot-password">
              {t('auth.forgot_password')}
            </Link>
          </div>

          <div className="method">
            <Button
              actionRole="primary"
              disabledMessage={isSigningIn ? t('auth.signing_in') : undefined}
              label={t('auth.continue_with_email')}
              type="submit"
            />
            {lastAuthMethod === 'email' && <span className="last-used">{t('auth.last_used')}</span>}
          </div>
        </form>

        <p className="separator">{t('auth.or')}</p>

        <div className="method">
          <Button
            actionRole="secondary"
            icon={<GoogleLogo />}
            label={t('auth.continue_with_google')}
            onClick={() => handleOAuthSignIn('google')}
          />
          {lastAuthMethod === 'google' && <span className="last-used">{t('auth.last_used')}</span>}
        </div>

        <div className="method">
          <Button
            actionRole="secondary"
            icon={<PaperPlaneTilt />}
            label={t('auth.email_me_a_code')}
            onClick={() => {
              void navigate('/auth/email-code')
            }}
          />
          {lastAuthMethod === 'code' && <span className="last-used">{t('auth.last_used')}</span>}
        </div>
      </div>
    </AuthLayout>
  )
}
